import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Activity, Appointment } from '../models/appointment';
import { Tag } from '../models/tag';

export interface AppointmentState {
  appointments: Appointment[];
  activities: Activity[];
  selectedDate: Date | null;
  selectedDateForModal: string;
  selectedActivityName: string;
  isLoading: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AppointmentStateService {
  
  private initialState: AppointmentState = {
    appointments: [],
    activities: [],
    selectedDate: null,
    selectedDateForModal: '',
    selectedActivityName: '',
    isLoading: false
  };

  private stateSubject = new BehaviorSubject<AppointmentState>(this.initialState);
  public state$ = this.stateSubject.asObservable();

  /**
   * Obtém o estado atual
   */
  get currentState(): AppointmentState {
    return this.stateSubject.value;
  }

  /**
   * Atualiza o estado
   */
  private updateState(partialState: Partial<AppointmentState>): void {
    const currentState = this.currentState;
    const newState = { ...currentState, ...partialState };
    this.stateSubject.next(newState);
  }

  /**
   * Define os appointments
   */
  setAppointments(appointments: Appointment[]): void {
    this.updateState({ appointments });
  }

  /**
   * Adiciona um novo appointment
   */
  addAppointment(appointment: Appointment): void {
    const currentAppointments = this.currentState.appointments;
    this.updateState({ 
      appointments: [...currentAppointments, appointment] 
    });
  }

  /**
   * Atualiza um appointment existente
   */
  updateAppointment(updatedAppointment: Appointment): void {
    const currentAppointments = this.currentState.appointments;
    const updatedAppointments = currentAppointments.map(appointment => 
      appointment.id === updatedAppointment.id ? updatedAppointment : appointment
    );
    this.updateState({ appointments: updatedAppointments });
  }

  /**
   * Remove um appointment
   */
  removeAppointment(appointmentId: string): void {
    const currentAppointments = this.currentState.appointments;
    const filteredAppointments = currentAppointments.filter(
      appointment => appointment.id !== appointmentId
    );
    this.updateState({ appointments: filteredAppointments });
  }

  /**
   * Define as atividades
   */
  setActivities(activities: Activity[]): void {
    this.updateState({ activities });
  }

  /**
   * Define a data selecionada
   */
  setSelectedDate(date: Date | null): void {
    this.updateState({ selectedDate: date });
  }

  /**
   * Define a data formatada para o modal
   */
  setSelectedDateForModal(dateString: string): void {
    this.updateState({ selectedDateForModal: dateString });
  }

  /**
   * Define o nome da atividade selecionada
   */
  setSelectedActivityName(activityName: string): void {
    this.updateState({ selectedActivityName: activityName });
  }

  /**
   * Define o estado de loading
   */
  setLoading(isLoading: boolean): void {
    this.updateState({ isLoading });
  }

  /**
   * Limpa todos os dados
   */
  clearAll(): void {
    this.updateState(this.initialState);
  }

  /**
   * Limpa apenas os appointments e atividades
   */
  clearAppointmentsAndActivities(): void {
    this.updateState({ 
      appointments: [], 
      activities: [] 
    });
  }

  /**
   * Busca um appointment por ID
   */
  findAppointmentById(id: string): Appointment | undefined {
    return this.currentState.appointments.find(appointment => appointment.id === id);
  }

  /**
   * Busca appointments por atividade e data
   */
  findAppointmentsByActivityAndDate(activityId: string, date: Date): Appointment[] {
    const dateString = date.toDateString();
    return this.currentState.appointments.filter(appointment => 
      appointment.activityId === activityId && 
      new Date(appointment.createdAt).toDateString() === dateString
    );
  }

  /**
   * Busca appointments por data
   */
  findAppointmentsByDate(date: Date): Appointment[] {
    const dateString = date.toDateString();
    return this.currentState.appointments.filter(appointment => 
      new Date(appointment.createdAt).toDateString() === dateString
    );
  }

  /**
   * Obtém observables específicos
   */
  get appointments$(): Observable<Appointment[]> {
    return new Observable(observer => {
      this.state$.subscribe(state => observer.next(state.appointments));
    });
  }

  get activities$(): Observable<Activity[]> {
    return new Observable(observer => {
      this.state$.subscribe(state => observer.next(state.activities));
    });
  }

  get selectedDate$(): Observable<Date | null> {
    return new Observable(observer => {
      this.state$.subscribe(state => observer.next(state.selectedDate));
    });
  }

  get isLoading$(): Observable<boolean> {
    return new Observable(observer => {
      this.state$.subscribe(state => observer.next(state.isLoading));
    });
  }

  get selectedDateForModal$(): Observable<string> {
    return new Observable(observer => {
      this.state$.subscribe(state => observer.next(state.selectedDateForModal));
    });
  }

  get selectedActivityName$(): Observable<string> {
    return new Observable(observer => {
      this.state$.subscribe(state => observer.next(state.selectedActivityName));
    });
  }
}
